import React, { useState, useCallback, useMemo } from 'react';
import styles from './UsageAnalytics.module.scss';
import Header from 'src/components/header/Header';
import { UsageEnum, SelectOption, UserRoleEnum } from '@src/types';
import CloseIcon from 'src/assets/icons/close.svg?react';
import IconRotating from 'src/assets/icons/rotating_arrow.svg?react';
import Button from '@src/components/button/Button';
import TabLayout from '@src/pages/admin/usage/tabLayout/TabLayout';
import SelectModal from './SelectModal/SelectModal';
import moment from 'moment';
import { newAUStandaloneLessonAgeGroupOptions, countryOptions, NAStandaloneLessonAgeGroupOptions } from '@src/constants';
import UsageView from '@src/pages/admin/usage/UsageView/UsageView';
import EngagementView from '@src/pages/admin/usage/EngagementView/EngagementView';
import SearchLayout from '@src/pages/admin/usage/filterLayout/SearchLayout';
import FooterPage from '@src/components/footer-page/FooterPage';

function UsageAnalytics() {

  const [currentMode, setCurrentMode] = useState(UsageEnum.Usage)
  const [account, setAccount] = useState<SelectOption>();
  const [country, setCountry] = useState<SelectOption>();
  const [schools, setSchools] = useState<SelectOption[]>();
  const [showSetting, setShowSetting] = useState(schools ? false : true)

  const today = moment();
  const startYear = today.month() < 7 ? today.year() - 1 : today.year(); // If current month is before August (0-6), use last year
  const startingDate = country?.value === countryOptions[1].value ? new Date(startYear, 0, 21) : new Date(startYear, 7, 1);   // August 1 for NA schools, Jan 21st for Aus Schools

  const [dateRange, setDateRange] = useState<any[]>([moment(startingDate).format('YYYY-MM-DD'), moment(new Date()).format('YYYY-MM-DD')]);
  const [grade, setGrade] = useState<SelectOption | undefined>();
  const [school, setSchool] = useState<SelectOption>({ label: "All Schools", value: "All Schools" });

  const [filters, setFilters] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [showFilterMenu, setShowFilterMenu] = useState(false);

  const [focused, setFocused] = useState(false);
  const [showGradeMenu, setShowGradeMenu] = useState(false);
  const [showSchoolMenu, setShowSchoolMenu] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);

  const [gradeResult, setGradeResult] = useState<SelectOption | undefined>();
  const [searchResult, setSearchResult] = useState('');

  const isAussie = useMemo(() => {
    return country?.value === countryOptions[1].value
  }, [country]);

  const filterSchools = useMemo(() => {
    if (school?.value === 'All Schools') {
      return schools?.map(option => option.value).filter(school =>  school !== 'All Schools') ?? ['']
    } else {
      return [school?.value ?? '']
    }
  }, [schools, school?.value])


  const filterGrades = useMemo(() => {
    const filterGrades = isAussie ? newAUStandaloneLessonAgeGroupOptions : NAStandaloneLessonAgeGroupOptions;
    if (gradeResult) {
      const gradeValues = filterGrades.map(program => {
        if (program.label === gradeResult.label) {
          return program.value
        } else {
          return ""
        }
      })
      return gradeValues.filter(Boolean)
    }
    return filterGrades.map(grade => grade.label);
  }, [gradeResult, isAussie])

  const handleSelectUsageMode = useCallback((mode: UsageEnum) => {
    setCurrentMode(mode)
  }, [])


  const handleSave = useCallback((userType: UserRoleEnum, account?: SelectOption, schools?: SelectOption[], country?: SelectOption) => {
    if (country?.value === countryOptions[1].value) {
      const today = moment();
      const startingDate = new Date(today.year(), 0, 21);   // Jan 21st for Aus Schools
      setDateRange([moment(startingDate).format('YYYY-MM-DD'), dateRange[1]])
    } else {
      const today = moment();
      const startYear = today.month() < 7 ? today.year() - 1 : today.year(); // If current month is before August (0-6), use last year
      const startingDate = new Date(startYear, 7, 1);     // August 1 for NA schools
      setDateRange([moment(startingDate).format('YYYY-MM-DD'), dateRange[1]])
    }
    setAccount(account);
    setSchools(schools);
    setShowSetting(false);
    setCountry(country);
  }, [dateRange])

  const handleSelectGrade = useCallback((grade: SelectOption) => {
    setGrade(grade);
  }, [])

  const handleSelectSchool = useCallback((school: SelectOption) => {
    setSchool(school);
  }, [])

  const handleChangeDate = useCallback((dateString: string[]) => {
    if (dateString.length > 0) {
      setDateRange(dateString)
    }
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters([]);
    setGrade(undefined);
    setSearch('');
    setGradeResult(undefined);
    setSearchResult('');
  }, []);

  const handleShowFilters = () => {
    setShowDatePicker(current => !current);
  }

  const handleChangeKeyword = useCallback((value: string) => {
    setSearch(value);
  }, []);

  const handleFilterApply = useCallback(() => {
    var filterOptions: string[] = []
    if (grade) {
      setGradeResult(grade)
      filterOptions.push(`Grade: ${grade?.label}`)
    }

    if (search) {
      setSearchResult(search)
      filterOptions.push(`Keyword: ${search}`)
    }
    setFilters(filterOptions);
    setShowFilterMenu(false);
  }, [grade, search])

  const handleClickDrop = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setShowFilterMenu(false);
  }

  const handleRemoveFilters = useCallback((value: string[]) => {
    const ageGroups = value.filter(group => group.includes('Grade:'))
    if (ageGroups.length > 0) {
      setGrade(undefined)
      setGradeResult(undefined)
    }

    const changedKeyword = value.filter(group => group.includes('Keyword:'))
    if (changedKeyword.length > 0) {
      setSearch('')
      setSearchResult('')
    }

    if (value.length === 0) {
      handleResetFilters();
    } else {
      setFilters(value);
    }
  }, [handleResetFilters])

  const handleRemoveTag = (removedTag: string) => {
    const newTags = filters.filter((tag) => tag !== removedTag);
    handleRemoveFilters(newTags);
  };

  const handleDatePicker = (open: boolean) => {
    setShowDatePicker(open);
  }
  const searchViewStyle = useMemo(() => {
    const classes = [styles.searchView];
    // remove search bar
    // if (showFilterMenu) {
    //   classes.push(styles.filterShow);
    // }
    return classes.join(' ');
  }, [])

  const currentPage = useMemo(() => {
    switch(currentMode) {
      case UsageEnum.Usage:
        return <UsageView
          grade={gradeResult} school={school} filterGrades={filterGrades} isAussie={isAussie} isSuperAdmin={true}
          startDate={moment.utc(dateRange[0]).format('YYYY-MM-DD HH:mm:ss')} endDate={moment.utc(dateRange[1]).format('YYYY-MM-DD HH:mm:ss')} filterSchools={filterSchools}
          dateRange={dateRange} datePickerVisible={showDatePicker}
        />;
      case UsageEnum.Engagement:
        return <EngagementView
          grade={gradeResult} school={school} filterGrades={filterGrades}
          startDate={moment.utc(dateRange[0]).format('YYYY-MM-DD HH:mm:ss')} endDate={moment.utc(dateRange[1]).format('YYYY-MM-DD HH:mm:ss')} filterSchools={filterSchools}
          dateRange={dateRange} datePickerVisible={showDatePicker} isSuperAdmin={true} keyword={searchResult}
        />;
      default:
        return null;
    }
  }, [currentMode, showDatePicker, dateRange, gradeResult, school, isAussie, filterSchools, filterGrades, searchResult])

  return (
    <>
      <Header />
      <div className={styles.wrapper}>
        <div className={[styles.backdrop, showFilterMenu ? styles.mfadeIn : styles.mfadeOut].join(' ')} onClick={handleClickDrop} />
        <div className={searchViewStyle}>
          <SearchLayout
            focused={focused}
            setFocused={setFocused}
            showGradeMenu={showGradeMenu}
            setShowGradeMenu={setShowGradeMenu}
            showSchoolMenu={showSchoolMenu}
            setShowSchoolMenu={setShowSchoolMenu}
            showDatePicker={showDatePicker}
            setShowDatePicker={handleDatePicker}
            filters={filters}
            search={search}
            gradeOptions={isAussie ? newAUStandaloneLessonAgeGroupOptions : NAStandaloneLessonAgeGroupOptions}
            selGrade={grade}
            startDate={dateRange[0]}
            endDate={dateRange[1]}
            dateRange={dateRange}
            onDateChange={handleChangeDate}
            handleSelectGrade={handleSelectGrade}
            handleSelectSchool={handleSelectSchool}
            handleChangeKeyword={handleChangeKeyword}
            handleApplySearch={handleFilterApply}
          />
        </div>
        {(focused || showGradeMenu || showSchoolMenu || showDatePicker) && <div className={[styles.menuBackdrop, showFilterMenu ? styles.mfadeIn : styles.mfadeOut].join(' ')} />}
        <div className={styles.page}>
          <div className={styles.leftView} >
            <div id="mainScrollView" className={styles.backView}>
              <TabLayout
                usageMode={currentMode}
                showFilterMenu={showFilterMenu}
                handleSelectUsageMode={handleSelectUsageMode}
                handleFilterClick={handleShowFilters}
                filters={filters}
                startDate={dateRange[0]}
                endDate={dateRange[1]}
                dateRange={dateRange}
                onDateChange={handleChangeDate}
                showDatePicker={showDatePicker}
                setShowDatePicker={handleDatePicker}
                showSchoolMenu={showSchoolMenu}
                setShowSchoolMenu={setShowSchoolMenu}
                selSchool={school}
                schoolOptions={schools}
                handleSelectSchool={handleSelectSchool}
              />
              <div className={styles.userSelectView}>
                <div className={styles.subView}>
                  {(schools && schools?.length > 1) ?
                  <div className={styles.selectedTitle}>{schools ? `You are currently viewing ${account?.label}.`: 'Please select school first.'}</div> :
                  <div className={styles.selectedTitle}>{schools ? `You are currently viewing ${account?.label} - ${schools[0]?.label}.`: 'Please select school first.'}</div>}
                  <Button outline={true} label={schools ? 'Reset': 'Select'} size='small' className={styles.resetButton} onClick={() => {setShowSetting(true)}}/>
                </div>
              </div>
              <div className={styles.mainView}>
              <div style={{display: 'none'}}>
                {filters.length > 0 ?
                  <div className={styles.searchResult}>
                    <div className={styles.searchResultTag}>
                      <span className={styles.resultTitle}>Results for</span>
                        <div className={styles.searchedTags}>
                        {filters.map((tag, index) => {
                          if (tag.includes('Grade')) {
                            var grade = tag.replace('Grade: ', '')
                            return <div
                              className={styles.searchTag}
                              key={index}
                              onClick={() => handleRemoveTag(tag)}>
                                <span>{grade}</span>
                                <CloseIcon className={styles.tagClose}/>
                            </div>
                          }
                          const keyword = tag.replace('Keyword: ', '')
                          const isLongTag = keyword.length > 30;
                          return <div
                            className={styles.searchThemeTag}
                            key={index}
                            onClick={() => handleRemoveTag(tag)}
                            >
                              <span>{isLongTag ? `${keyword.slice(0, 30)}...` : keyword}</span>
                              <CloseIcon className={styles.themeTagClose}/>
                          </div>
                          })
                        }
                      </div>
                    </div>
                    <Button onClick={handleResetFilters} className={styles.resetBtn} label={'Reset'}
                      size={'extraSmall'} outline={true} color={'red'}
                      leftIcon={<IconRotating className={styles.resetIcon}/>}/>
                  </div> : null
                }
                </div>
                {schools && currentPage}
              </div>
            </div>
          </div>
        </div>
        <FooterPage />
      </div>
      {showSetting &&
      <SelectModal open={showSetting} onClose={() => setShowSetting(false)} onSave={handleSave}/>}
    </>
  );
}

export default UsageAnalytics;
